import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
/**
 * cart-items — CONFIG-BY-CONFIG port of Shrine's `main-cart-items` cart SECTION
 * (sections/main-cart-items.liquid). Shrine's own section schema carries ONLY 5 settings — a "Bundles"
 * header + two bundle toggles (native-Shopify bundle products), and the section-padding header + two
 * padding ranges. It declares NO blocks[] and NO presets: the cart_* theme blocks (Subtotals, Checkout
 * button, Discount field, Note, Terms, Checkpoints/Progress bar, Countdown, Gift, Upsells) are
 * container-level blocks hosted by `container--cart` / the cart drawer, NOT children of this section —
 * those live in our sibling `cart-footer` section. This section renders the cart LINE-ITEM list +
 * the EMPTY-cart state.
 *
 * Because the line-item row in Shrine is a fixed Liquid partial (no per-setting schema), and a
 * settings-only port of just the 5 native settings would give the seller almost nothing to tune, we add
 * a small, faithful set of LINE-ITEM-ROW controls that mirror what the Shrine cart row actually exposes
 * (title link, vendor/variant/properties visibility, image ratio/size, quantity stepper, remove control,
 * unit-price line, empty-state copy + continue-shopping link). Every Shrine native setting is present and
 * every control below is WIRED into the render. Bundle settings are Shopify-platform (item.item_components)
 * — surfaced for parity, documented as deferred via `info`.
 *
 * className prefix in the Component: `aqx-cti-`. Colors come from theme tokens only (hex only in a color
 * default). `templates: ['cart']` — this section lives on the cart page.
 */
export const cartItemsSchema = {
    type: 'cart-items',
    name: 'Cart items',
    version: 1,
    templates: ['cart'],
    fields: [
        // ── Heading — the page title (05/10/2026) ─────────────────────────────────
        // "My cart" used to be painted by the storefront's /cart page itself, outside the document, in the
        // theme's ink on the theme's background: a black band the seller could not reach from the builder.
        // It is the section's own heading now — hers to word, align or hide.
        {
            id: 'showHeading',
            type: 'checkbox',
            label: 'Show the page title',
            group: 'Heading',
            default: true,
        },
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: 'Heading',
            info: 'Leave empty to keep your shop’s own wording, in your shop’s language.',
            default: '',
        },
        {
            id: 'headingAlign',
            type: 'select',
            label: 'Heading alignment',
            group: 'Heading',
            default: 'center',
            options: [
                { value: 'start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'end', label: 'Right' },
            ],
        },
        // ── Header (table heads) ───────────────────────────────────────────────────
        {
            id: 'showHeaderRow',
            type: 'checkbox',
            label: 'Show column headings',
            group: 'Header',
            info: 'The "Product / Price / Quantity / Total" heading row above the line items (desktop only; cards stack on mobile).',
            default: true,
        },
        {
            id: 'productHeading',
            type: 'text',
            label: 'Product column label',
            group: 'Header',
            default: 'Produit',
            defaultKey: 'cart.product_column',
            visibleWhen: { field: 'showHeaderRow', equals: true },
        },
        {
            id: 'priceHeading',
            type: 'text',
            label: 'Price column label',
            group: 'Header',
            default: 'Prix',
            defaultKey: 'cart.price_column',
            visibleWhen: { field: 'showHeaderRow', equals: true },
        },
        {
            id: 'quantityHeading',
            type: 'text',
            label: 'Quantity column label',
            group: 'Header',
            default: 'Quantité',
            defaultKey: 'cart.quantity',
            visibleWhen: { field: 'showHeaderRow', equals: true },
        },
        {
            id: 'totalHeading',
            type: 'text',
            label: 'Total column label',
            group: 'Header',
            default: 'Total',
            defaultKey: 'cart.total',
            visibleWhen: { field: 'showHeaderRow', equals: true },
        },
        // ── Product image ──────────────────────────────────────────────────────────
        {
            id: 'showImage',
            type: 'checkbox',
            label: 'Show product image',
            group: 'Product image',
            default: true,
        },
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'Product image',
            default: 'square',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'portrait', label: 'Portrait (4:5)' },
                { value: 'square', label: 'Square (1:1)' },
            ],
            visibleWhen: { field: 'showImage', equals: true },
        },
        {
            id: 'imageSize',
            type: 'range',
            label: 'Image size',
            group: 'Product image',
            default: 88,
            min: 56,
            max: 140,
            step: 4,
            unit: 'px',
            visibleWhen: { field: 'showImage', equals: true },
        },
        {
            id: 'imageCornerRadius',
            type: 'range',
            label: 'Image corner radius',
            group: 'Product image',
            default: 0,
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            visibleWhen: { field: 'showImage', equals: true },
        },
        // ── Product details ────────────────────────────────────────────────────────
        {
            id: 'titleLink',
            type: 'checkbox',
            label: 'Link title to product page',
            group: 'Product details',
            default: true,
        },
        {
            id: 'titleSize',
            type: 'range',
            label: 'Title size',
            group: 'Product details',
            default: 16,
            min: 12,
            max: 24,
            step: 1,
            unit: 'px',
        },
        {
            id: 'showVendor',
            type: 'checkbox',
            label: 'Show vendor',
            group: 'Product details',
            default: false,
        },
        {
            id: 'showVariant',
            type: 'checkbox',
            label: 'Show variant / option label',
            group: 'Product details',
            default: true,
        },
        {
            id: 'showProperties',
            type: 'checkbox',
            label: 'Show line-item properties',
            group: 'Product details',
            info: 'Custom line-item properties (engraving, gift message, …). Shopify writes these on add-to-cart; the demo cart shows a sample property for parity.',
            default: true,
        },
        {
            id: 'showSku',
            type: 'checkbox',
            label: 'Show SKU',
            group: 'Product details',
            info: 'Variant SKU. Read from the line variant (Shopify item.sku) — hidden in the demo cart when absent.',
            default: false,
        },
        // ── Unit price ─────────────────────────────────────────────────────────────
        {
            id: 'showUnitPrice',
            type: 'checkbox',
            label: 'Show unit price',
            group: 'Unit price',
            info: 'The per-unit price beside the line total (and per-measurement unit price where Shopify provides one).',
            default: true,
        },
        {
            id: 'showComparePrice',
            type: 'checkbox',
            label: 'Show compare-at (struck) price',
            group: 'Unit price',
            default: true,
        },
        {
            id: 'priceColor',
            type: 'select',
            label: 'Price color',
            group: 'Unit price',
            default: 'text',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'text', label: 'Text' },
            ],
        },
        // ── Quantity ───────────────────────────────────────────────────────────────
        {
            id: 'showQuantity',
            type: 'checkbox',
            label: 'Show quantity selector',
            group: 'Quantity',
            info: 'The +/- quantity stepper. Live cart mutation (/cart/change) is wired to the storefront cart layer later — the control renders now and updates the previewed line total locally.',
            default: true,
        },
        {
            id: 'quantityStyle',
            type: 'select',
            label: 'Quantity selector style',
            group: 'Quantity',
            default: 'stepper',
            options: [
                { value: 'stepper', label: 'Stepper (− value +)' },
                { value: 'input', label: 'Number input' },
            ],
            visibleWhen: { field: 'showQuantity', equals: true },
        },
        {
            id: 'accentColor',
            type: 'select',
            label: 'Stepper accent color',
            group: 'Quantity',
            default: 'text',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'text', label: 'Text' },
            ],
            visibleWhen: { field: 'showQuantity', equals: true },
        },
        // ── Remove control ─────────────────────────────────────────────────────────
        {
            id: 'removeStyle',
            type: 'select',
            label: 'Remove control',
            group: 'Remove',
            default: 'icon',
            options: [
                { value: 'icon', label: 'Trash icon' },
                { value: 'text', label: 'Text link' },
                { value: 'both', label: 'Icon + text' },
            ],
        },
        {
            id: 'removeLabel',
            type: 'text',
            label: 'Remove label',
            group: 'Remove',
            default: 'Retirer',
            defaultKey: 'cart.remove',
            visibleWhen: { field: 'removeStyle', notEquals: 'icon' },
        },
        // ── Row layout ─────────────────────────────────────────────────────────────
        {
            id: 'rowSeparator',
            type: 'checkbox',
            label: 'Divider between rows',
            group: 'Row layout',
            default: true,
        },
        {
            id: 'rowSpacing',
            type: 'range',
            label: 'Row spacing',
            group: 'Row layout',
            default: 20,
            min: 8,
            max: 48,
            step: 2,
            unit: 'px',
        },
        // ── Bundles (native Shopify bundle products) ───────────────────────────────
        {
            id: 'bundleTitleSuffix',
            type: 'text',
            label: 'Bundle title suffix',
            group: 'Bundles',
            info: 'Shopify-platform (native bundle products / item.item_components): appended to a bundle parent line. Surfaced for parity; bundle component rendering is deferred.',
            default: '+ [BUNDLE]',
        },
        {
            id: 'bundleShowImages',
            type: 'checkbox',
            label: 'Show bundle component images',
            group: 'Bundles',
            info: 'Shopify-platform (bundle components / item.item_components). Surfaced for parity; deferred.',
            default: false,
        },
        // ── Empty state ────────────────────────────────────────────────────────────
        {
            id: 'emptyHeading',
            type: 'text',
            label: 'Empty-cart heading',
            group: 'Empty state',
            default: 'Votre panier est vide',
            defaultKey: 'cart.empty_title',
        },
        {
            id: 'emptyText',
            type: 'text',
            label: 'Empty-cart message',
            group: 'Empty state',
            default: "Vous n'avez encore rien ajouté.",
            defaultKey: 'cart.empty_body',
        },
        {
            id: 'continueLabel',
            type: 'text',
            label: 'Continue-shopping button label',
            group: 'Empty state',
            default: 'Continuer mes achats',
            defaultKey: 'cart.continue_shopping',
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal: scheme + padding + width) ─────────────────────────
        // Shrine's main-cart-items padding_top / padding_bottom (0–100 step 4; cart.json overrides 0 / 24)
        // are covered by the universal padding fields (defaults set to 36 / 24 in the preset below).
        ...universalFields,
    ],
    blocks: [],
};
export const cartItemsPresets = [
    {
        name: 'Cart items',
        settings: {
            showHeading: true,
            heading: '',
            headingAlign: 'center',
            showHeaderRow: true,
            showImage: true,
            imageRatio: 'square',
            imageSize: 88,
            imageCornerRadius: 8,
            titleLink: true,
            titleSize: 16,
            showVendor: false,
            showVariant: true,
            showProperties: true,
            showSku: false,
            showUnitPrice: true,
            showComparePrice: true,
            priceColor: 'text',
            showQuantity: true,
            quantityStyle: 'stepper',
            accentColor: 'text',
            removeStyle: 'icon',
            rowSeparator: true,
            rowSpacing: 20,
            bundleTitleSuffix: '+ [BUNDLE]',
            bundleShowImages: false,
            // Shrine cart.json overrides: padding_top 0, padding_bottom 24 → here a touch of top room.
            paddingTop: 36,
            paddingBottom: 24,
            width: 'standard',
        },
        blocks: [],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const cartItemsMeta = {
    label: 'Cart items',
    group: 'Cart',
    icon: 'cart',
};
//# sourceMappingURL=schema.js.map